import React from 'react';
import styles from './BranchLifecycleSpine.module.css';
import Ico from '../common/Ico';
import { STATUS_KINDS, statusKind } from './statusConfig';

const ICO_BRANCH  = 'M6 3v12 M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 9a9 9 0 0 1-9 9';
const ICO_MOON    = 'M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36A5.39 5.39 0 0 1 12.5 3.1 9.06 9.06 0 0 0 12 3z';
const ICO_ROCKET  = 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z';

const NIGHTLY_TEXT = { ok: 'complete', info: 'running', warn: 'pending', err: 'failed', neutral: 'unknown' };

function deriveStageStatus(branch, stageName, counts) {
  if (stageName === 'branching') {
    const ops = Object.values(branch.branching_ops || {});
    const total = ops.length;
    const completed = ops.filter(o => o.status === 'completed').length;
    if (!total) return { status: 'neutral', text: 'Not started', badgeText: null };
    if (ops.every(o => o.status === 'completed')) return { status: 'ok', text: 'All branched', badgeText: `${total} comps` };
    if (ops.some(o => o.status === 'errored')) return { status: 'err', text: 'Some failed', badgeText: `${completed}/${total}` };
    return { status: 'info', text: 'In progress', badgeText: `${completed}/${total}` };
  }
  if (stageName === 'nightly') {
    const n = counts.nightlyTotal;
    const latest = counts.latestNightlyRun;
    if (!n || !latest) return { status: 'neutral', text: 'No runs yet', badgeText: null };
    const st = statusKind(latest.status);
    return { status: st, text: `Nightly #${latest.number} · ${NIGHTLY_TEXT[st] || latest.status}`, badgeText: `${n} run${n !== 1 ? 's' : ''}` };
  }
  if (stageName === 'releases') {
    const releases = counts.releases;
    const n = releases.length;
    if (!n) return { status: 'neutral', text: 'No releases yet', badgeText: null };
    const latest = releases.reduce((a, b) => (a.version > b.version ? a : b), releases[0]);
    const st = statusKind(latest.status);
    return { status: st, text: `Release ${latest.version} · ${st === 'ok' ? 'released' : latest.status}`, badgeText: `${n} release${n !== 1 ? 's' : ''}` };
  }
  return { status: 'neutral', text: '—', badgeText: null };
}

const STAGES = [
  { key: 'branching', name: 'Branching', icon: ICO_BRANCH, anchor: 'sec-branching', summary: 'Component repos branched from main when the branch was created.' },
  { key: 'nightly',   name: 'Nightly',   icon: ICO_MOON,   anchor: 'sec-nightly',   summary: 'Build · AUTs · E2E · deploy to test instance · deploy to POV.' },
  { key: 'releases',  name: 'Releases',  icon: ICO_ROCKET, anchor: 'sec-releases',  summary: 'Official tag + published artifacts.' },
];

export default function BranchLifecycleSpine({ branch, nightlyTotal = 0, latestNightlyRun = null, releases = [] }) {
  const counts = { nightlyTotal, latestNightlyRun, releases };

  const scrollTo = (anchor) => {
    document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.inner}>
        <div className={styles.label}>
          <Ico d='M22 12h-4l-3 9L9 3l-3 9H2' size={14} style={{ color: 'var(--fg-tertiary)' }} />
          <span className={styles.labelText}>Branch lifecycle</span>
          <span className={styles.labelHint}>— everything derives from the branch, left to right</span>
        </div>
        <div className={styles.spine}>
          {STAGES.map((stage, i) => {
            const { status, text, badgeText } = deriveStageStatus(branch, stage.key, counts);
            const tok = STATUS_KINDS[status];
            return (
              <React.Fragment key={stage.key}>
                <button
                  className={styles.card}
                  onClick={() => scrollTo(stage.anchor)}
                  type="button"
                >
                  <div className={styles.cardTop}>
                    <span
                      className={styles.iconTile}
                      style={{ background: tok.bg, color: tok.fg }}
                    >
                      <Ico d={stage.icon} size={16} strokeWidth={1.7} />
                    </span>
                    <span className={styles.cardName}>{stage.name}</span>
                    {badgeText && (
                      <span
                        className={styles.badge}
                        style={{ background: tok.bg, color: tok.fg, borderColor: tok.bd }}
                      >
                        {badgeText}
                      </span>
                    )}
                  </div>
                  <div className={styles.cardSummary}>{stage.summary}</div>
                  <div className={styles.cardStatus}>
                    <span className={styles.statusDot} style={{ background: tok.dot }} />
                    <span style={{ color: tok.fg, fontSize: 11.5, fontWeight: 600 }}>{text}</span>
                  </div>
                </button>
                {i < STAGES.length - 1 && (
                  <span className={styles.connector}>
                    <svg width="24" height="14" viewBox="0 0 26 14" fill="none">
                      <path d="M1 7h20M17 3l4 4-4 4" stroke="#A4A7AE" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
